import { useEffect, useState, type FormEvent } from 'react';
import { MapPin, CalendarBlank, Users, MagnifyingGlass } from '@phosphor-icons/react';
import type { SearchSort } from '@pms/shared';
import { SortControl } from './SortControl';

interface SearchSummaryProps {
  destination: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  total: number;
  isFetching: boolean;
  sort: SearchSort;
  onSearch: (next: { destination: string; checkIn: string; checkOut: string; guests: number }) => void;
  onSortChange: (s: SearchSort) => void;
}

/** Editable search bar + live result count + sort, shown above the results. */
export function SearchSummary(props: SearchSummaryProps) {
  const [destination, setDestination] = useState(props.destination);
  const [checkIn, setCheckIn] = useState(props.checkIn);
  const [checkOut, setCheckOut] = useState(props.checkOut);
  const [guests, setGuests] = useState(props.guests);

  // Keep inputs synced if the URL changes externally (back/forward).
  useEffect(() => {
    setDestination(props.destination);
    setCheckIn(props.checkIn);
    setCheckOut(props.checkOut);
    setGuests(props.guests);
  }, [props.destination, props.checkIn, props.checkOut, props.guests]);

  const dateInvalid = !!checkIn && !!checkOut && checkOut <= checkIn;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (dateInvalid) return;
    onSearchTrim();
  }
  function onSearchTrim() {
    props.onSearch({ destination: destination.trim(), checkIn, checkOut, guests });
  }

  const place = props.destination.trim();

  return (
    <div className="space-y-4">
      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-2 rounded-[1.5rem] border border-sand bg-white p-2 shadow-[0_24px_50px_-40px_rgba(28,26,23,0.4)] lg:grid-cols-[2fr_1.1fr_1.1fr_auto_auto]"
      >
        <Field icon={<MapPin className="h-5 w-5 text-clay" />} label="Where to">
          <input
            type="text" value={destination} onChange={(e) => setDestination(e.target.value)}
            placeholder="Anywhere" className="w-full bg-transparent text-ink outline-none"
          />
        </Field>
        <Field icon={<CalendarBlank className="h-5 w-5 text-clay" />} label="Check in">
          <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
            aria-label="Check-in date" className="w-full bg-transparent text-ink outline-none" />
        </Field>
        <Field icon={<CalendarBlank className="h-5 w-5 text-clay" />} label="Check out">
          <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
            aria-label="Check-out date" className="w-full bg-transparent text-ink outline-none" />
        </Field>
        <Field icon={<Users className="h-5 w-5 text-clay" />} label="Guests">
          <input type="number" min={1} value={guests} onChange={(e) => setGuests(Number(e.target.value))}
            aria-label="Number of guests" className="w-14 bg-transparent text-ink outline-none" />
        </Field>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 rounded-2xl bg-clay px-6 py-3 font-semibold text-white transition hover:bg-clay-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2"
        >
          <MagnifyingGlass className="h-5 w-5" /> Search
        </button>
      </form>
      {dateInvalid && (
        <p role="alert" className="text-sm font-medium text-red-600">
          Check-out must be after check-in.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-serif text-2xl font-bold text-ink sm:text-3xl" aria-live="polite">
          {props.isFetching && props.total === 0
            ? 'Searching…'
            : `${props.total} ${props.total === 1 ? 'hotel' : 'hotels'}${place ? ` in ${place}` : ''}`}
        </p>
        <SortControl value={props.sort} onChange={props.onSortChange} />
      </div>
    </div>
  );
}

function Field({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-3 rounded-2xl px-3 py-2 transition hover:bg-cream-deep">
      {icon}
      <span className="flex min-w-0 flex-col">
        <span className="text-xs font-semibold text-ink-muted">{label}</span>
        {children}
      </span>
    </label>
  );
}
